import { RefObject } from 'react';

import { ErrorEventsEnum } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

/**
 * Stops MediaRecorder and waits for stop event
 * Ensures all data chunks are collected before resolving
 * Returns cleanup function to cancel pending timeouts
 */
export function stopMediaRecorder(mediaRecorder: MediaRecorder): {
  cleanup: () => void;
  promise: Promise<void>;
} {
  const timeoutIds: ReturnType<typeof setTimeout>[] = [];
  let stopResolved = false;

  const cleanup = () => {
    timeoutIds.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    timeoutIds.length = 0;
  };

  const promise = new Promise<void>((resolve) => {
    const recorder = mediaRecorder;

    if (
      typeof recorder.state === 'undefined' ||
      recorder.state !== 'inactive'
    ) {
      const resolveOnce = () => {
        if (stopResolved) return;

        stopResolved = true;
        cleanup();
        resolve();
      };

      recorder.onstop = () => {
        const timeoutId = setTimeout(() => {
          resolveOnce();
        }, 100);
        timeoutIds.push(timeoutId);
      };

      if (recorder.state === 'recording') {
        if (typeof recorder.requestData === 'function') {
          recorder.requestData();
        }

        recorder.stop();
      } else {
        resolveOnce();
      }

      const fallbackTimeoutId = setTimeout(() => {
        resolveOnce();
      }, 2000);
      timeoutIds.push(fallbackTimeoutId);
    } else {
      resolve();
    }
  });

  return { cleanup, promise };
}

/**
 * Sets up MediaRecorder error handler
 */
export function setupMediaRecorderErrorHandler(
  mediaRecorder: MediaRecorder
): void {
  // eslint-disable-next-line no-param-reassign
  mediaRecorder.onerror = (event) => {
    const error =
      (event as ErrorEvent).error || new Error('MediaRecorder error');

    logErrorToSentry({
      error: error instanceof Error ? error : new Error(String(error)),
      errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
      errorName: 'MEDIARECORDER_ERROR',
      source: 'mediaRecorderUtils.setupMediaRecorderErrorHandler',
    });
  };
}

/**
 * Starts MediaRecorder with fallback logic
 */
export function startMediaRecorder(mediaRecorder: MediaRecorder): void {
  try {
    if (mediaRecorder.state === 'inactive') {
      mediaRecorder.start(100);
    }
  } catch (startError) {
    try {
      mediaRecorder.start();
    } catch (fallbackError) {
      throw new Error(
        `Failed to start MediaRecorder: ${
          fallbackError instanceof Error
            ? fallbackError.message
            : String(fallbackError)
        }`
      );
    }
  }
}

/**
 * Cleans up MediaRecorder and microphone stream
 */
export function cleanupMediaRecorder(
  mediaRecorderRef: RefObject<MediaRecorder | null>,
  micStreamRef: RefObject<MediaStream | null>,
  audioChunksRef: RefObject<Blob[]>
): void {
  if (mediaRecorderRef.current) {
    const recorder = mediaRecorderRef.current;

    if (
      typeof recorder.state === 'undefined' ||
      recorder.state !== 'inactive'
    ) {
      if (typeof recorder.requestData === 'function') {
        recorder.requestData();
      }

      recorder.stop();
    }
  }

  // eslint-disable-next-line no-param-reassign
  mediaRecorderRef.current = null;

  if (micStreamRef.current) {
    try {
      micStreamRef.current.getTracks().forEach((track) => {
        if (track.readyState !== 'ended') {
          track.stop();
        }
      });
    } catch (error) {
      logErrorToSentry({
        error: error instanceof Error ? error : new Error(String(error)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'MIC_STREAM_TRACK_STOP_FAILED',
        source: 'mediaRecorderUtils.cleanupMediaRecorder',
      });
    }
    // eslint-disable-next-line no-param-reassign
    micStreamRef.current = null;
  }

  // eslint-disable-next-line no-param-reassign
  audioChunksRef.current = [];
}
